import { useEffect, useMemo, useState } from 'react'
import { apiClient } from '@/lib/api/client'

export type SupportTypeOption = {
  value: string
  label: string
}

type SupportTypeApiRow = {
  id?: number
  code: string
  label?: string | null
  sort_order?: number
  active?: boolean
}

let cachedOptions: SupportTypeOption[] | null = null
let cachedLabelByCode: Record<string, string> | null = null
let fetchPromise: Promise<SupportTypeOption[]> | null = null

function normalizeRows(payload: unknown): SupportTypeApiRow[] {
  if (Array.isArray(payload)) return payload as SupportTypeApiRow[]
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: SupportTypeApiRow[] }).data
  }
  return []
}

function mapRows(rows: SupportTypeApiRow[]): SupportTypeOption[] {
  return rows
    .filter((row) => row?.code)
    .filter((row) => row.active !== false)
    .map((row) => ({
      value: row.code,
      label: row.label?.trim() || row.code,
    }))
}

async function fetchSupportTypeRows(): Promise<SupportTypeApiRow[]> {
  try {
    const { data } = await apiClient.get<unknown>('/options/support-types')
    const rows = normalizeRows(data)
    if (rows.length > 0) return rows
  } catch {
    // Fall through to service-catalog list (same auth, no active.client)
  }

  const { data } = await apiClient.get<unknown>('/service-catalog/support-types', {
    params: { per_page: 100, order: 'sort_order', sort: 'asc' },
  })
  return normalizeRows(data)
}

async function loadSupportTypes(): Promise<SupportTypeOption[]> {
  const rows = await fetchSupportTypeRows()
  const options = mapRows(rows)
  cachedOptions = options
  cachedLabelByCode = Object.fromEntries(options.map((o) => [o.value, o.label]))
  return options
}

function ensureSupportTypesLoaded(): Promise<SupportTypeOption[]> {
  if (cachedOptions !== null) return Promise.resolve(cachedOptions)
  if (!fetchPromise) {
    fetchPromise = loadSupportTypes().finally(() => {
      fetchPromise = null
    })
  }
  return fetchPromise
}

export function invalidateSupportTypeOptionsCache(): void {
  cachedOptions = null
  cachedLabelByCode = null
  fetchPromise = null
}

export function useSupportTypeOptions() {
  const [options, setOptions] = useState<SupportTypeOption[]>(cachedOptions ?? [])
  const [loading, setLoading] = useState(cachedOptions === null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(cachedOptions === null)
    ensureSupportTypesLoaded()
      .then((loaded) => {
        if (cancelled) return
        setOptions(loaded)
        setError(null)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const message = e instanceof Error ? e.message : 'Failed to load support types'
        setError(message)
        setOptions([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const labelByCode = useMemo(
    () => cachedLabelByCode ?? Object.fromEntries(options.map((o) => [o.value, o.label])),
    [options]
  )

  return { options, labelByCode, loading, error }
}

export function resolveSupportTypeLabel(
  code: string | null | undefined,
  labelByCode: Record<string, string>
): string | undefined {
  if (!code) return undefined
  return labelByCode[code] ?? code
}

/** Ensure select shows the current ticket value even if inactive or not yet in catalog list. */
export function mergeSupportTypeSelectOptions(
  options: SupportTypeOption[],
  currentValue: string | undefined,
  labelByCode: Record<string, string>
): SupportTypeOption[] {
  const current = currentValue?.trim()
  if (!current || options.some((o) => o.value === current)) {
    return options
  }
  return [{ value: current, label: labelByCode[current] ?? current }, ...options]
}
